import { Trans, useTranslation } from "react-i18next";

import { Button } from "../../../../../../components/Button";
import * as Fonts from "../../../../../../components/Fonts";
import { KeepoLoader } from "../../../../../../components/KeepoLoader";
import ArrowRightIcon from "../../../../../../icons/ArrowRight";
import { findDomainProvider } from "../../../../../../model/domainProviders";
import { CLOUDFLARE_PROXY_HELP_URL, CustomDomain } from "../../../../../../services/customDomain";
import { Card, MainAction, Note, Section, Spinner, Steps, TextLink } from "../../styles";
import { DnsRecords } from "../DnsRecords";

type Props = {
  domain: CustomDomain;
  onConfirm: () => void;
  onChangeDomain: () => void;
};

// "<anchor>" in the copy (not "<link>", a void HTML tag the parser would close empty)
const ExternalLink = ({ href, children }: { href?: string; children?: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

/** Second step: the DNS records to create at the provider, then the wait while they are checked. */
export const SetupDomain = ({ domain, onConfirm, onChangeDomain }: Props) => {
  const { t } = useTranslation();
  const provider = findDomainProvider(domain.provider);
  const isChecking = domain.status === "verifying";
  const isSubdomain = domain.type === "subdomain";
  // Subdomains only need a CNAME: use the provider's CNAME guide when it has one
  const tutorialUrl = (isSubdomain && provider?.cnameTutorialUrl) || provider?.tutorialUrl;

  const prefix = "pages.settings.customDomain.setup";

  // A domain bought at Keepo: Keepo set its DNS already, so no steps and nothing to change, just the wait (the
  // certificate can take a while); the page keeps checking until it's connected
  if (domain.managedByKeepo) {
    return (
      <>
        <Section>
          <Card aria-busy="true">
            <h3>{t("pages.settings.customDomain.keepoDomain.setup.title", { domain: domain.domain })}</h3>
            <p className="lead">{t("pages.settings.customDomain.keepoDomain.setup.text")}</p>
            <span className="loader">
              <KeepoLoader size={24} color="var(--color-neutral-white)" />
            </span>
          </Card>
        </Section>
      </>
    );
  }

  return (
    <>
      <Section>
        <Card>
          <h3>{isChecking ? t(`${prefix}.waitingTitle`) : t(`${prefix}.title`)}</h3>
          <p className="lead">{t(`${prefix}.description`, { domain: domain.domain })}</p>
          <Note>{t(isSubdomain ? `${prefix}.typeSubdomain` : `${prefix}.typeRoot`, { domain: domain.domain })}</Note>
          <Steps>
            <li>
              <ArrowRightIcon />
              <span>
                {provider?.dnsUrl ? (
                  <Trans
                    i18nKey={`${prefix}.steps.access`}
                    values={{ provider: provider.name }}
                    components={{ anchor: <ExternalLink href={provider.dnsUrl} /> }}
                  />
                ) : (
                  t(`${prefix}.steps.accessGeneric`)
                )}
              </span>
            </li>
            <li>
              <ArrowRightIcon />
              <span>
                {tutorialUrl ? (
                  <Trans
                    i18nKey={`${prefix}.steps.records`}
                    values={{ provider: provider.name }}
                    components={{ anchor: <ExternalLink href={tutorialUrl} /> }}
                  />
                ) : (
                  t(`${prefix}.steps.recordsGeneric`)
                )}
              </span>
            </li>
            <li>
              <ArrowRightIcon />
              <span>{t(`${prefix}.steps.confirm`)}</span>
            </li>
          </Steps>
          {provider?.key === "cloudflare" && (
            <Note>
              <Trans
                i18nKey={`${prefix}.cloudflareProxyTip`}
                components={{ anchor: <ExternalLink href={CLOUDFLARE_PROXY_HELP_URL} /> }}
              />
            </Note>
          )}
          <Note>{t(`${prefix}.propagation`)}</Note>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t("pages.settings.customDomain.records.section")}</Fonts.Label>
        <DnsRecords records={domain.records} checking={isChecking} />
      </Section>

      <MainAction>
        <Button type="INFO" size="LG" disabled={isChecking} onClick={onConfirm}>
          {isChecking && <Spinner />}
          {isChecking ? t(`${prefix}.waiting`) : t(`${prefix}.confirm`)}
        </Button>
        <TextLink type="button" onClick={onChangeDomain}>
          {t(`${prefix}.change`)}
        </TextLink>
      </MainAction>
    </>
  );
};
